import { z } from "zod";
import {
  ATTRIBUTION_CHANNELS,
  CANCEL_REASONS,
  CONFIRMATION_STATES,
  DATE_PRESETS,
  ORDER_SOURCES,
  ORDER_STATUSES,
  PAYMENT_METHODS,
  AD_PLATFORMS,
} from "../enums";
import type {
  AdPlatform,
  AttributionChannel,
  CancelReason,
  CodState,
  ConfirmationMethod,
  ConfirmationState,
  OrderSource,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  ReturnStatus,
  ReturnType,
  OnlinePaymentProvider,
  OnlinePaymentStatus,
  ShipmentStatus,
} from "../enums";

// Orders (Commandes). Prices always come from the catalog on the server: inputs carry product ids and quantities,
// never prices. Amounts are integer minor units in the client currency.

const id = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const minor = z.number().int("Montant invalide").min(0, "Montant invalide");
const boolQuery = z.enum(["true", "false"]).transform((v) => v === "true");
const optionalEmail = z
  .union([z.string().trim().toLowerCase().email("Adresse e-mail invalide").max(254), z.literal("")])
  .nullable()
  .optional();

export const orderItemInputSchema = z.object({
  productId: id,
  variantId: id.nullable().optional(),
  qty: z.number().int("Quantité invalide").min(1, "Quantité minimum 1").max(1000, "Quantité maximum 1000"),
});
export type OrderItemInput = z.infer<typeof orderItemInputSchema>;

export const orderAddressSchema = z.object({
  name: z.string().trim().max(120).optional().default(""),
  phone: z.string().trim().max(40).optional().default(""),
  governorate: z.string().trim().min(1, "Gouvernorat requis").max(80),
  delegation: z.string().trim().max(80).optional().default(""),
  city: z.string().trim().min(1, "Ville requise").max(80),
  line1: z.string().trim().min(1, "Adresse requise").max(240),
  zip: z.string().trim().max(12).optional().default(""),
  notes: z.string().trim().max(500).optional().default(""),
  country: z.string().length(2).toUpperCase().optional(),
});
export type OrderAddressInput = z.infer<typeof orderAddressSchema>;

export const orderCustomerInputSchema = z.object({
  id: id.nullable().optional(),
  name: z.string().trim().min(2, "Nom du client requis").max(120),
  phone: z.string().trim().min(6, "Téléphone requis").max(40),
  email: optionalEmail,
});

export const orderAttributionInputSchema = z.object({
  channel: z.enum(ATTRIBUTION_CHANNELS).optional(),
  platform: z.enum(AD_PLATFORMS).nullable().optional(),
  campaignId: z.string().trim().max(64).nullable().optional(),
  adsetId: z.string().trim().max(64).nullable().optional(),
  adId: z.string().trim().max(64).nullable().optional(),
  utm: z
    .object({
      source: z.string().max(200).optional(),
      medium: z.string().max(200).optional(),
      campaign: z.string().max(200).optional(),
      content: z.string().max(200).optional(),
      term: z.string().max(200).optional(),
    })
    .optional(),
  clickIds: z
    .object({
      fbclid: z.string().max(500).optional(),
      fbc: z.string().max(500).optional(),
      fbp: z.string().max(500).optional(),
      ttclid: z.string().max(500).optional(),
      ttp: z.string().max(500).optional(),
      gclid: z.string().max(500).optional(),
      ctwaClid: z.string().max(500).optional(),
    })
    .optional(),
  landingUrl: z.string().max(2000).nullable().optional(),
  referrer: z.string().max(2000).nullable().optional(),
});

export const orderSourceRefSchema = z.object({
  conversationId: id.nullable().optional(),
  leadId: id.nullable().optional(),
  formId: id.nullable().optional(),
  siteId: id.nullable().optional(),
  pageId: id.nullable().optional(),
});

export const orderCreateSchema = z.object({
  customer: orderCustomerInputSchema,
  items: z.array(orderItemInputSchema).min(1, "Ajoutez au moins un produit").max(50, "50 lignes maximum"),
  shippingAddress: orderAddressSchema,
  paymentMethod: z.enum(PAYMENT_METHODS).optional().default("cod"),
  source: z.enum(ORDER_SOURCES).optional().default("manual"),
  marketCode: z.string().trim().length(2).toUpperCase().optional(),
  discountMinor: minor.optional().default(0),
  /** null or absent = computed from the market shipping fee table. */
  shippingFeeMinor: minor.nullable().optional(),
  applyQuantityOffers: z.boolean().optional().default(true),
  customerNote: z.string().trim().max(1000).optional().default(""),
  internalNote: z.string().trim().max(2000).optional().default(""),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional().default([]),
  attribution: orderAttributionInputSchema.optional(),
  /** Where the order comes from (conversation, lead, form, landing page), stored as references on the order. */
  sourceRef: orderSourceRefSchema.optional(),
  /** "Créer et confirmer": the agent confirmed the order with the customer. */
  confirm: z.boolean().optional().default(false),
});
export type OrderCreateInput = z.infer<typeof orderCreateSchema>;

export const orderQuoteSchema = z.object({
  items: z.array(orderItemInputSchema).max(50),
  governorate: z.string().trim().max(80).optional().default(""),
  marketCode: z.string().trim().length(2).toUpperCase().optional(),
  discountMinor: minor.optional().default(0),
  shippingFeeMinor: minor.nullable().optional(),
  applyQuantityOffers: z.boolean().optional().default(true),
});
export type OrderQuoteInput = z.infer<typeof orderQuoteSchema>;

export const orderUpdateSchema = z.object({
  items: z.array(orderItemInputSchema).min(1, "Ajoutez au moins un produit").max(50).optional(),
  shippingAddress: orderAddressSchema.optional(),
  discountMinor: minor.optional(),
  shippingFeeMinor: minor.nullable().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  customerNote: z.string().trim().max(1000).optional(),
  internalNote: z.string().trim().max(2000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type OrderUpdateInput = z.infer<typeof orderUpdateSchema>;

export const orderConfirmSchema = z.object({
  note: z.string().trim().max(500).optional().default(""),
});

export const orderCancelSchema = z.object({
  reason: z.enum(CANCEL_REASONS, { errorMap: () => ({ message: "Motif d'annulation requis" }) }),
  note: z.string().trim().max(500).optional().default(""),
});
export type OrderCancelInput = z.infer<typeof orderCancelSchema>;

/** Results an agent (or the confirmation flow) can log for one confirmation attempt. */
export const CONFIRMATION_ATTEMPT_RESULTS = ["requested", "no_answer", "postponed", "unreachable", "refused"] as const;
export type ConfirmationAttemptResult = (typeof CONFIRMATION_ATTEMPT_RESULTS)[number];

export const orderConfirmationAttemptSchema = z
  .object({
    result: z.enum(CONFIRMATION_ATTEMPT_RESULTS),
    note: z.string().trim().max(500).optional().default(""),
    postponedUntil: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .refine((v) => v.result !== "postponed" || Boolean(v.postponedUntil), { message: "Date de rappel requise", path: ["postponedUntil"] });
export type OrderConfirmationAttemptInput = z.infer<typeof orderConfirmationAttemptSchema>;

/** Manual status changes. Shipments drive these statuses when the order has an active shipment. */
export const MANUAL_ORDER_STATUSES = ["preparing", "shipped", "delivered", "returned"] as const;
export type ManualOrderStatus = (typeof MANUAL_ORDER_STATUSES)[number];

export const orderStatusChangeSchema = z.object({
  status: z.enum(MANUAL_ORDER_STATUSES),
  note: z.string().trim().max(500).optional().default(""),
});

export const ORDER_BULK_ACTIONS = ["confirm", "cancel", "preparing", "export"] as const;
export type OrderBulkAction = (typeof ORDER_BULK_ACTIONS)[number];

export const orderBulkSchema = z
  .object({
    action: z.enum(ORDER_BULK_ACTIONS),
    ids: z.array(id).min(1, "Sélectionnez au moins une commande").max(500, "500 commandes maximum"),
    reason: z.enum(CANCEL_REASONS).optional(),
    note: z.string().trim().max(500).optional().default(""),
  })
  .refine((v) => v.action !== "cancel" || Boolean(v.reason), { message: "Motif d'annulation requis", path: ["reason"] });
export type OrderBulkInput = z.infer<typeof orderBulkSchema>;

export const ORDER_SORTS = ["recent", "oldest", "total_desc", "total_asc"] as const;

export const orderListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum([...ORDER_STATUSES, "all"] as const).default("all"),
  late: boolQuery.optional(),
  confirmation: z.enum(CONFIRMATION_STATES).optional(),
  source: z.enum(ORDER_SOURCES).optional(),
  channel: z.enum(ATTRIBUTION_CHANNELS).optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  customerId: id.optional(),
  preset: z.enum(DATE_PRESETS).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  sort: z.enum(ORDER_SORTS).default("recent"),
  /** Comma separated ids (export of a selection). */
  ids: z
    .string()
    .max(500 * 25)
    .optional()
    .transform((v) => (v ? v.split(",").map((s) => s.trim()).filter((s) => /^[a-f0-9]{24}$/i.test(s)) : undefined)),
});
export type OrderListQuery = z.infer<typeof orderListQuery>;
export type OrderListQueryInput = z.input<typeof orderListQuery>;

export interface OrderItemDto {
  id: string;
  productId: string | null;
  variantId: string | null;
  sku: string;
  name: string;
  variantTitle: string;
  imageUrl: string | null;
  qty: number;
  unitPriceMinor: number;
  totalMinor: number;
  /** Label of the catalog quantity offer applied to this line ("" when none). */
  offerLabel: string;
  /** What the line would cost without the offer (null when no offer applied). */
  regularTotalMinor: number | null;
}

export interface OrderTimelineEntryDto {
  type: string;
  at: string;
  actor: { type: string; id: string | null; name: string | null };
  message: string;
  data: Record<string, unknown> | null;
}

export interface OrderListItemDto {
  id: string;
  number: string;
  status: OrderStatus;
  isLate: boolean;
  customerId: string | null;
  customer: { name: string; phone: string; email: string };
  itemsCount: number;
  itemsPreview: string;
  firstItemImage: string | null;
  totalMinor: number;
  currency: string;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  codState: CodState;
  confirmationState: ConfirmationState;
  confirmationAttempts: number;
  postponedUntil: string | null;
  source: OrderSource;
  attributionChannel: AttributionChannel;
  attributionPlatform: AdPlatform | null;
  carrierCode: string | null;
  trackingNumber: string | null;
  shipmentStatus: ShipmentStatus | null;
  governorate: string;
  city: string;
  risk: { score: number | null; level: "low" | "medium" | "high" | null; factors: string[] } | null;
  tags: string[];
  createdAt: string;
  expectedDeliveryAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
}

export interface OrderDto extends OrderListItemDto {
  items: OrderItemDto[];
  subtotalMinor: number;
  discountMinor: number;
  shippingFeeMinor: number;
  shippingFeeOverridden: boolean;
  codAmountMinor: number;
  refundedMinor: number;
  confirmation: {
    state: ConfirmationState;
    method: ConfirmationMethod | null;
    attempts: number;
    lastAttemptAt: string | null;
    postponedUntil: string | null;
    confirmedAt: string | null;
    note: string;
  };
  cancelReason: CancelReason | null;
  cancelNote: string;
  shippingAddress: { name: string; phone: string; governorate: string; delegation: string; city: string; line1: string; zip: string; notes: string; country: string };
  marketCode: string;
  sourceRef: { conversationId: string | null; leadId: string | null; formId: string | null; siteId: string | null; pageId: string | null };
  attribution: {
    channel: AttributionChannel;
    platform: AdPlatform | null;
    campaignId: string | null;
    adsetId: string | null;
    adId: string | null;
    campaignName: string | null;
    utm: { source?: string; medium?: string; campaign?: string; content?: string; term?: string };
    clickIds: { fbclid?: string; fbc?: string; fbp?: string; ttclid?: string; ttp?: string; gclid?: string; ctwaClid?: string };
    landingUrl: string | null;
    referrer: string | null;
  };
  shipmentId: string | null;
  confirmedAt: string | null;
  returnedAt: string | null;
  cancelledAt: string | null;
  internalNote: string;
  customerNote: string;
  timeline: OrderTimelineEntryDto[];
  createdBy: { type: string; id: string | null; name: string | null };
  updatedAt: string;
  /** Statuses reachable from the current one with a manual action. */
  allowedTransitions: OrderStatus[];
  /** Items, address and fees can be edited (status new). */
  editable: boolean;
  returns: Array<{ id: string; type: ReturnType; status: ReturnStatus; createdAt: string }>;
  payments: Array<{ id: string; provider: OnlinePaymentProvider; status: OnlinePaymentStatus; amountMinor: number; paymentUrl: string | null; expiresAt: string | null; paidAt: string | null; createdAt: string }>;
}

export type OrderCountsDto = Record<OrderStatus | "all" | "late", number>;

export interface OrderQuoteLineDto {
  productId: string;
  variantId: string | null;
  name: string;
  variantTitle: string;
  imageUrl: string | null;
  qty: number;
  unitPriceMinor: number;
  totalMinor: number;
  offerLabel: string;
  regularTotalMinor: number | null;
  /** Units available when inventory is tracked (null = not tracked). */
  stockAvailable: number | null;
}

export interface OrderQuoteDto {
  lines: OrderQuoteLineDto[];
  subtotalMinor: number;
  discountMinor: number;
  shippingFeeMinor: number;
  autoShippingFeeMinor: number;
  freeShipping: boolean;
  zone: string | null;
  etaDays: number;
  totalMinor: number;
  currency: string;
  marketCode: string;
  codEnabled: boolean;
  warnings: string[];
}

export interface OrderBulkResultDto {
  ok: string[];
  failed: Array<{ id: string; number: string | null; message: string }>;
}

/** Manual transitions of the order status machine (shipments drive shipped / delivered / returned otherwise). */
export const ORDER_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  new: ["confirmed", "cancelled"],
  confirmed: ["preparing", "shipped", "cancelled"],
  preparing: ["shipped", "cancelled"],
  shipped: ["delivered", "returned"],
  delivered: ["returned"],
  returned: [],
  cancelled: [],
};

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_TRANSITIONS[from].includes(to);
}
